import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, MarkerType, useReactFlow,
  applyNodeChanges, applyEdgeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import JourneyNode from './JourneyNode';
import { NODE_DEFS, ADDABLE_TYPES } from '../../data/nodeDefinitions';
import Icon from '../ui/Icon';
import styles from './JourneyCanvas.module.css';

const NODE_TYPES = { journeyNode: JourneyNode };
const NODE_W = 200;
const NODE_H = 40; // half-height offset so a dropped node centers under the cursor

function toRFNodes(nodes, selectedId, selectedIds, onDelete, metricsByNodeId, segmentNames) {
  return nodes.map((n) => ({
    id: n.id,
    type: 'journeyNode',
    position: { x: n.x, y: n.y },
    selected: n.id === selectedId || (selectedIds || []).includes(n.id),
    data: { nodeType: n.type, cfg: n.cfg, onDelete, metrics: metricsByNodeId ? metricsByNodeId[n.id] : undefined, segmentNames },
  }));
}

function toRFEdges(edges, selectedEdgeId, edgeLabels) {
  return (edges || []).map((e) => {
    const label = edgeLabels && edgeLabels[e.id] !== undefined ? String(edgeLabels[e.id]) : undefined;
    return {
      id: e.id,
      source: e.from,
      target: e.to,
      sourceHandle: e.fromHandle || undefined,
      targetHandle: e.toHandle || undefined,
      selected: e.id === selectedEdgeId,
      markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--b2)' },
      label,
      labelBgStyle: label !== undefined ? { fill: 'var(--s1)', fillOpacity: 0.95 } : undefined,
      labelStyle: label !== undefined ? { fill: 'var(--t1)', fontWeight: 700, fontSize: 11 } : undefined,
      labelBgPadding: label !== undefined ? [4, 2] : undefined,
      labelBgBorderRadius: 4,
    };
  });
}

// Shared floating node picker for quick-add (drop a connector on empty canvas)
// and insert-on-edge. Lists only mid-flow-insertable node types (no entry).
function QuickPicker({ x, y, onPick, onClose }) {
  return (
    <>
      <div className={styles.pickerBackdrop} onClick={onClose} />
      <div className={styles.picker} style={{ left: x, top: y }}>
        <div className={styles.pickerHead}>Add step</div>
        {ADDABLE_TYPES.map((type) => {
          const def = NODE_DEFS[type];
          return (
            <button key={type} className={styles.pickerItem} onClick={() => onPick(type)}>
              <span className={styles.pickerIco} style={{ background: def.icoC, color: def.icoT }}>
                <Icon name={def.ico} size={14} />
              </span>
              {def.label}
            </button>
          );
        })}
      </div>
    </>
  );
}

function CanvasInner(props) {
  const {
    nodes, edges, selectedId, selectedIds, onSelect, onSelectionChange, onInteractStart,
    onMove, onDelete, onDeselect, onConnect, onEdgeDelete, onAddAt, onQuickAdd, onInsertEdge,
    readOnly, defaultViewport, onViewportChange, canUndo, canRedo, onUndo, onRedo,
    metricsByNodeId, edgeLabels, onLiveNodeClick, segmentNames,
  } = props;
  const { screenToFlowPosition, flowToScreenPosition, fitView, setCenter } = useReactFlow();
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [picker, setPicker] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');

  const draggingRef = useRef(false);
  const connectedRef = useRef(false);
  const connectStartRef = useRef(null);

  const [rfNodes, setRfNodes] = useState(() => toRFNodes(nodes, selectedId, selectedIds, onDelete, metricsByNodeId, segmentNames));
  const [rfEdges, setRfEdges] = useState(() => toRFEdges(edges, selectedEdgeId, edgeLabels));

  useEffect(() => { setRfNodes(toRFNodes(nodes, selectedId, selectedIds, onDelete, metricsByNodeId, segmentNames)); }, [nodes, selectedId, selectedIds, onDelete, metricsByNodeId, segmentNames]);
  useEffect(() => { setRfEdges(toRFEdges(edges, selectedEdgeId, edgeLabels)); }, [edges, selectedEdgeId, edgeLabels]);

  const handleNodesChange = useCallback((changes) => {
    setRfNodes((current) => applyNodeChanges(changes, current));
    changes.forEach((change) => {
      if (change.type === 'position' && change.dragging === true) {
        if (!draggingRef.current) { draggingRef.current = true; onInteractStart && onInteractStart(); }
      } else if (change.type === 'position' && change.dragging === false) {
        if (change.position) onMove(change.id, change.position.x, change.position.y);
        draggingRef.current = false;
      } else if (change.type === 'remove') {
        onDelete(change.id);
      }
    });
  }, [onMove, onDelete, onInteractStart]);

  const handleEdgesChange = useCallback((changes) => {
    setRfEdges((current) => applyEdgeChanges(changes, current));
    changes.forEach((change) => {
      if (change.type === 'remove') {
        onEdgeDelete(change.id);
        setSelectedEdgeId((cur) => (cur === change.id ? null : cur));
      }
    });
  }, [onEdgeDelete]);

  const handleConnectStart = useCallback((_, params) => {
    connectedRef.current = false;
    connectStartRef.current = { nodeId: params.nodeId, handleId: params.handleId };
  }, []);

  const handleConnect = useCallback((connection) => {
    connectedRef.current = true;
    onConnect(connection.source, connection.sourceHandle || null, connection.target);
  }, [onConnect]);

  // Dropped a connector on empty canvas (no target handle) → offer a quick-add
  // picker there, then wire the new node straight off the source handle.
  const handleConnectEnd = useCallback((event) => {
    const src = connectStartRef.current;
    connectStartRef.current = null;
    if (readOnly || connectedRef.current || !src?.nodeId) return;
    const point = event.changedTouches ? event.changedTouches[0] : event;
    const flow = screenToFlowPosition({ x: point.clientX, y: point.clientY });
    setPicker({ kind: 'quickadd', screenX: point.clientX, screenY: point.clientY, flowX: flow.x - NODE_W / 2, flowY: flow.y, source: src });
  }, [screenToFlowPosition, readOnly]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (readOnly) return;
    const nodeType = e.dataTransfer.getData('application/journey-node-type');
    if (!nodeType || !NODE_DEFS[nodeType]) return;
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    onAddAt(nodeType, position.x - NODE_W / 2, position.y - NODE_H / 2);
  }, [screenToFlowPosition, onAddAt, readOnly]);

  const handleMoveEnd = useCallback((_, viewport) => { onViewportChange && onViewportChange(viewport); }, [onViewportChange]);

  const handleSelectionChange = useCallback(({ nodes: sel }) => {
    onSelectionChange && onSelectionChange((sel || []).map((n) => n.id));
  }, [onSelectionChange]);

  const onPick = useCallback((type) => {
    if (!picker) return;
    if (picker.kind === 'quickadd') onQuickAdd(type, picker.flowX, picker.flowY, picker.source.nodeId, picker.source.handleId);
    else if (picker.kind === 'insert') onInsertEdge(picker.edgeId, type);
    setPicker(null);
    setSelectedEdgeId(null);
  }, [picker, onQuickAdd, onInsertEdge]);

  // Insert-on-edge affordance: a "+" button at the midpoint of the selected edge.
  const selectedEdge = edges.find((e) => e.id === selectedEdgeId);
  let insertBtnPos = null;
  if (!readOnly && selectedEdge) {
    const from = nodes.find((n) => n.id === selectedEdge.from);
    const to = nodes.find((n) => n.id === selectedEdge.to);
    if (from && to) {
      const mid = flowToScreenPosition({ x: (from.x + to.x) / 2 + NODE_W / 2, y: (from.y + to.y) / 2 + NODE_H });
      insertBtnPos = mid;
    }
  }

  const openInsert = useCallback((screenX, screenY) => {
    setPicker({ kind: 'insert', screenX, screenY, edgeId: selectedEdgeId });
  }, [selectedEdgeId]);

  const q = query.trim().toLowerCase();
  const searchResults = q
    ? nodes.filter((n) => {
        const def = NODE_DEFS[n.type] || {};
        return (def.label + ' ' + n.type + ' ' + JSON.stringify(n.cfg || {})).toLowerCase().includes(q);
      }).slice(0, 8)
    : nodes.slice(0, 8);

  const focusNode = useCallback((n) => {
    setCenter(n.x + NODE_W / 2, n.y + NODE_H, { zoom: 1, duration: 400 });
    if (!readOnly) onSelect(n.id);
    setSearchOpen(false);
    setQuery('');
  }, [setCenter, onSelect, readOnly]);

  return (
    <div className={styles.wrap} onDrop={handleDrop} onDragOver={handleDragOver}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={handleConnect}
        onConnectStart={handleConnectStart}
        onConnectEnd={handleConnectEnd}
        onMoveEnd={handleMoveEnd}
        onSelectionChange={handleSelectionChange}
        onNodeClick={(_, node) => { readOnly && onLiveNodeClick ? onLiveNodeClick(node.id) : onSelect(node.id); setSelectedEdgeId(null); }}
        onEdgeClick={(_, edge) => { if (!readOnly) setSelectedEdgeId(edge.id); }}
        onPaneClick={() => { setSelectedEdgeId(null); setPicker(null); onDeselect(); }}
        deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
        multiSelectionKeyCode={['Meta', 'Control']}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
        {...(defaultViewport ? { defaultViewport } : { fitView: true })}
        minZoom={0.3}
        maxZoom={1.5}
      >
        <Background variant="dots" gap={24} size={1} color="var(--b1)" />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable style={{ background: 'var(--s2)' }} maskColor="rgba(0,0,0,0.06)" />
      </ReactFlow>

      {/* Canvas toolbar — undo/redo/fit/search */}
      <div className={styles.toolbar}>
        {!readOnly && (
          <>
            <button className={styles.toolBtn} onClick={onUndo} disabled={!canUndo} title="Undo (⌘Z)"><Icon name="undo" size={17} /></button>
            <button className={styles.toolBtn} onClick={onRedo} disabled={!canRedo} title="Redo (⌘⇧Z)"><Icon name="redo" size={17} /></button>
            <div className={styles.toolSep} />
          </>
        )}
        <button className={styles.toolBtn} onClick={() => fitView({ padding: 0.2, duration: 400 })} title="Fit to screen"><Icon name="fit_screen" size={17} /></button>
        <button className={[styles.toolBtn, searchOpen ? styles.toolBtnOn : ''].join(' ')} onClick={() => setSearchOpen((v) => !v)} title="Find a node"><Icon name="search" size={17} /></button>
      </div>

      {searchOpen && (
        <div className={styles.searchPanel}>
          <input
            className={styles.searchInput}
            autoFocus
            placeholder="Find a node…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className={styles.searchList}>
            {searchResults.length === 0
              ? <div className={styles.searchEmpty}>No matching nodes</div>
              : searchResults.map((n) => {
                  const def = NODE_DEFS[n.type] || {};
                  return (
                    <button key={n.id} className={styles.searchItem} onClick={() => focusNode(n)}>
                      <span className={styles.pickerIco} style={{ background: def.icoC, color: def.icoT }}><Icon name={def.ico} size={13} /></span>
                      <span className={styles.searchItemLabel}>{def.label}</span>
                    </button>
                  );
                })}
          </div>
        </div>
      )}

      {insertBtnPos && !picker && (
        <button
          className={styles.insertBtn}
          style={{ left: insertBtnPos.x, top: insertBtnPos.y }}
          onClick={() => openInsert(insertBtnPos.x, insertBtnPos.y)}
          title="Insert a node into this connection"
        >
          <Icon name="add" size={16} />
        </button>
      )}

      {picker && (
        <QuickPicker x={picker.screenX} y={picker.screenY} onPick={onPick} onClose={() => setPicker(null)} />
      )}

      {nodes.length === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyIco}>
            <span className="material-symbols-rounded" style={{ fontSize: 40 }}>route</span>
          </div>
          <div className={styles.emptyTitle}>Canvas is empty</div>
          <div className={styles.emptySub}>Drag nodes from the left palette to start building your journey</div>
        </div>
      )}
    </div>
  );
}

// ReactFlowProvider is required for useReactFlow() (screenToFlowPosition,
// used by the palette drag/drop handler) to work inside CanvasInner.
function JourneyCanvas(props) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}

export default JourneyCanvas;
